import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, CardContent, MenuItem, Select, TextField, Typography, GridLegacy as Grid } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import apiClient from '../../services/apiClient';

type BillingRecord = {
  _id: string;
  billingId?: string;
  licensePlate?: string;
  amount?: { original?: number; discount?: number; final?: number };
  paymentMethod?: string;
  status?: string;
  timestamps?: { createdAt?: string; updatedAt?: string; completed?: string };
  parkingSession?: {
    entryTime?: string;
    exitTime?: string;
    duration?: { actual?: number };
    vehicle?: { licensePlate?: string };
  };
  paymentProvider?: string;
};

const PAGE_SIZE_OPTIONS = [20, 50, 100];

const MonthlyPaymentQuery: React.FC = () => {
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('day'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().endOf('day'));
  const [operator, setOperator] = useState<string>('全部');
  const [orderSource, setOrderSource] = useState<string>('全部');
  const [laneName, setLaneName] = useState<string>('全部');
  const [licensePlate, setLicensePlate] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<string>('monthly_pass');
  const [status, setStatus] = useState<string>('不限');
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [records, setRecords] = useState<BillingRecord[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const queryParams = useMemo(() => {
    const params: Record<string, string> = {
      page: String(page),
      limit: String(pageSize),
      sortBy: 'createdAt',
      sortOrder: 'desc',
      paymentMethod: paymentMethod,
    };
    if (startDate) params.startDate = startDate.toISOString();
    if (endDate) params.endDate = endDate.toISOString();
    if (licensePlate) params.licensePlate = licensePlate.trim();
    if (status !== '不限') params.status = status;
    return params;
  }, [page, pageSize, startDate, endDate, licensePlate, paymentMethod, status]);

  const fetchRecords = async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await apiClient.get('/billing/records', { params: queryParams });
      const payload = resp.data;
      if (payload?.success) {
        const records = payload.data?.records || [];
        const total = payload.data?.pagination?.totalRecords || 0;
        setRecords(records);
        setTotal(total);
      } else {
        throw new Error(payload?.message || '查詢失敗');
      }
    } catch (e: any) {
      setError(e?.message || '查詢失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const handleSearch = () => {
    setPage(1);
    fetchRecords();
  };

  const formatDuration = (mins?: number) => {
    if (!mins && mins !== 0) return '-';
    const h = Math.floor(mins / 60);
    const m = Math.floor(mins % 60);
    return `${h}小時${m}分`;
  };

  const paymentMethodOptions = [
    { value: 'monthly_pass', label: '月票/月租' },
    { value: 'cash', label: '現金' },
    { value: 'credit_card', label: '信用卡' },
    { value: 'debit_card', label: '金融卡' },
    { value: 'mobile_payment', label: '行動支付' },
    { value: 'bank_transfer', label: '銀行轉帳' },
    { value: 'digital_wallet', label: '電子錢包' },
  ];

  return (
    <Box p={2}>
      <Typography variant="h6" gutterBottom>
        月租車繳費查詢
      </Typography>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <DateTimePicker
                label="開始時間"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue as Dayjs | null)}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DateTimePicker
                label="截止時間"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue as Dayjs | null)}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField label="車牌號" value={licensePlate} onChange={(e) => setLicensePlate(e.target.value)} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Select fullWidth size="small" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value as string)} displayEmpty>
                {paymentMethodOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Select fullWidth size="small" value={status} onChange={(e) => setStatus(e.target.value as string)} displayEmpty>
                <MenuItem value="不限">訂單狀態：不限</MenuItem>
                <MenuItem value="completed">已支付</MenuItem>
                <MenuItem value="pending">待支付</MenuItem>
                <MenuItem value="failed">失敗</MenuItem>
              </Select>
            </Grid>

            {/* 其餘篩選 UI，先預留不影響查詢 */}
            <Grid item xs={12} sm={6} md={2}>
              <Select fullWidth size="small" value={operator} onChange={(e) => setOperator(e.target.value as string)} displayEmpty>
                <MenuItem value="全部">操作人員：全部</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Select fullWidth size="small" value={orderSource} onChange={(e) => setOrderSource(e.target.value as string)} displayEmpty>
                <MenuItem value="全部">訂單來源：全部</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Select fullWidth size="small" value={laneName} onChange={(e) => setLaneName(e.target.value as string)} displayEmpty>
                <MenuItem value="全部">崗亭名稱：全部</MenuItem>
              </Select>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <Button variant="contained" onClick={handleSearch} fullWidth>查詢</Button>
            </Grid>
          </Grid>
          </LocalizationProvider>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          {error && (
            <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
          )}
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {[
                    '序號','訂單編號','車牌號碼','訂單金額','應收金額','免費金額','折扣金額','繳費時間','付款方式','停車時長','訂單來源','訂單類型','進場時間','出場時間','24小時預留金額'
                  ].map((h) => (
                    <th key={h} style={{ textAlign: 'left', borderBottom: '1px solid #eee', padding: '8px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={15} style={{ padding: 16 }}>載入中...</td></tr>
                ) : records.length === 0 ? (
                  <tr><td colSpan={15} style={{ padding: 16 }}>無資料</td></tr>
                ) : (
                  records.map((r, idx) => {
                    const lp = r.licensePlate || r.parkingSession?.vehicle?.licensePlate || '-';
                    const orderId = r.billingId || r._id;
                    const created = r.timestamps?.completed || r.timestamps?.createdAt || r.timestamps?.updatedAt || '';
                    const entry = r.parkingSession?.entryTime || '';
                    const exit = r.parkingSession?.exitTime || '';
                    return (
                      <tr key={r._id}>
                        <td style={{ padding: '8px' }}>{(page - 1) * pageSize + idx + 1}</td>
                        <td style={{ padding: '8px' }}>{orderId}</td>
                        <td style={{ padding: '8px' }}>{lp}</td>
                        <td style={{ padding: '8px' }}>{r.amount?.original ?? '-'}</td>
                        <td style={{ padding: '8px' }}>{r.amount?.final ?? '-'}</td>
                        <td style={{ padding: '8px' }}>{0}</td>
                        <td style={{ padding: '8px' }}>{r.amount?.discount ?? 0}</td>
                        <td style={{ padding: '8px' }}>{created ? dayjs(created).format('YYYY/MM/DD HH:mm:ss') : '-'}</td>
                        <td style={{ padding: '8px' }}>{r.paymentMethod ?? '-'}</td>
                        <td style={{ padding: '8px' }}>{formatDuration(r.parkingSession?.duration?.actual)}</td>
                        <td style={{ padding: '8px' }}>{r.paymentProvider ?? '-'}</td>
                        <td style={{ padding: '8px' }}>月租車</td>
                        <td style={{ padding: '8px' }}>{entry ? dayjs(entry).format('YYYY/MM/DD HH:mm:ss') : '-'}</td>
                        <td style={{ padding: '8px' }}>{exit ? dayjs(exit).format('YYYY/MM/DD HH:mm:ss') : '-'}</td>
                        <td style={{ padding: '8px' }}>{0}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </Box>

          {/* Pagination */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mt={2}>
            <Box display="flex" gap={1} alignItems="center">
              <Button variant="outlined" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>上一頁</Button>
              <Typography>第 {page} 頁，共 {Math.max(1, Math.ceil(total / pageSize))} 頁</Typography>
              <Button variant="outlined" disabled={page >= Math.ceil(total / pageSize)} onClick={() => setPage((p) => p + 1)}>下一頁</Button>
            </Box>
            <Select size="small" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
              {PAGE_SIZE_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>每頁 {opt} 筆</MenuItem>
              ))}
            </Select>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default MonthlyPaymentQuery;